import { useEffect, useState } from 'react';

export default function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="bg-[#f5f1e8] text-[#1a1a1a] min-h-screen font-serif">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#f5f1e8]/95 backdrop-blur-sm border-b border-[#1a1a1a]/10' : ''}`}>
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="text-xs tracking-[0.3em] font-sans uppercase text-[#1a1a1a]/60">
              Night-only Coffee Stand
            </div>
          </div>
          <a 
            href="https://instagram.com/hoshizora1121" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs hover:text-[#8b0000] transition-colors cursor-pointer flex items-center gap-2 font-sans uppercase tracking-wider"
          >
            <i className="ri-instagram-line text-base"></i>
            <span>@hoshizora1121</span>
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
        
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-12 gap-12 items-start">
            {/* Left Column - Main Title */}
            <div className="md:col-span-7">
              <div className="mb-8">
                <div className="inline-block border-2 border-[#1a1a1a] px-4 py-2 mb-6 rotate-[-1deg]">
                  <span className="text-xs font-sans tracking-[0.3em] uppercase">Exhibition</span>
                </div>
              </div>
              
              <h1 className="text-7xl md:text-8xl font-bold tracking-tight leading-[0.9] mb-8 font-sans uppercase">
                HOSHIZORA<br />
                <span className="text-[#1a1a1a]/70">NIGHTS</span>
              </h1>
              
              <div className="border-l-4 border-[#1a1a1a] pl-6 mb-12">
                <h2 className="text-3xl md:text-4xl mb-4 font-serif">
                  伊香保星空珈琲
                </h2>
                <p className="text-lg md:text-xl text-[#1a1a1a]/80 leading-relaxed">
                  星が出てる日のみ、20時に灯る珈琲。
                </p>
              </div>

              {/* Image with caption */}
              <div className="relative rotate-[1deg] mb-8">
                <div className="bg-white p-3 shadow-lg">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/d0296b14fe478db41c349119913b0851.jpg"
                    alt="Night Coffee"
                    className="w-full h-[400px] object-cover object-top"
                  />
                  <p className="text-xs mt-2 text-[#1a1a1a]/60 font-sans">Fig.1 — A quiet cup under stars</p>
                </div>
              </div>
            </div>

            {/* Right Column - Press Clipping */}
            <div className="md:col-span-5 space-y-8">
              <div className="bg-white p-6 shadow-md border border-[#1a1a1a]/10 rotate-[-2deg]">
                <div className="text-xs font-sans uppercase tracking-wider mb-3 text-[#8b0000]">
                  Press Clipping
                </div>
                <p className="text-sm leading-relaxed mb-4 font-sans">
                  "Open only when the sky agrees."
                </p>
                <p className="text-sm leading-relaxed text-[#1a1a1a]/70 font-sans">
                  A coffee stand that appears on clear nights, offering warmth beneath the stars.
                </p>
              </div>

              <div className="bg-[#1a1a1a] text-white p-6 shadow-md rotate-[1deg]">
                <p className="text-sm leading-relaxed font-sans italic">
                  "In the quiet of night, a single light. A cup of coffee. The vast sky above."
                </p>
              </div>

              {/* Small image */}
              <div className="relative rotate-[-1deg]">
                <div className="bg-white p-2 shadow-md">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Minimalist%20black%20and%20white%20fine%20art%20photograph%20of%20brilliant%20starry%20night%20sky%20with%20countless%20stars%20scattered%20across%20dark%20cosmos%2C%20milky%20way%20galaxy%20visible%20as%20soft%20luminous%20band%20across%20frame%2C%20deep%20space%20astronomy%20image%20with%20high%20contrast%20between%20pitch%20black%20void%20and%20bright%20stellar%20points%2C%20contemplative%20and%20serene%20mood%20perfect%20for%20art%20gallery%20exhibition%2C%20grainy%20film%20texture%20with%20subtle%20noise%20adding%20vintage%20quality&width=600&height=400&seq=art-stars-night-002&orientation=landscape"
                    alt="Starry Sky"
                    className="w-full h-[250px] object-cover object-top"
                  />
                  <p className="text-xs mt-1 text-[#1a1a1a]/60 font-sans">Fig.2 — When the sky agrees</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statement Section */}
      <section className="py-20 px-6 bg-white border-y-2 border-[#1a1a1a]">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <h2 className="text-sm font-sans uppercase tracking-[0.3em] mb-2">Statement</h2>
              <div className="text-2xl font-serif mb-4">展示概要</div>
            </div>
            
            <div className="md:col-span-8">
              <p className="text-base md:text-lg leading-loose text-[#1a1a1a]/90 mb-6">
                伊香保の夜、星が輝く日だけ灯りをともすコーヒーショップ。観光で訪れた方も、地元で暮らす方も、ひとりでふらりと立ち寄れる場所。星空の下、あたたかいコーヒーを片手に、ゆっくりと流れる時間をお過ごしください。静けさと一杯のあたたかさが、夜の寄り道を特別なものにします。
              </p>
              
              <div className="inline-block border border-[#1a1a1a] px-4 py-2 text-xs font-sans uppercase tracking-wider">
                Night-only / Weather-dependent
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Section */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <h2 className="text-sm font-sans uppercase tracking-[0.3em] mb-2">Visit</h2>
              <div className="text-2xl font-serif mb-4">営業案内</div>
              
              {/* Decorative element */}
              <div className="mt-8 relative rotate-[2deg]">
                <div className="bg-white p-2 shadow-md border border-[#1a1a1a]/20">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Atmospheric%20black%20and%20white%20photograph%20of%20warm%20glowing%20lantern%20light%20illuminating%20rustic%20wooden%20coffee%20shop%20entrance%20at%20night%20with%20soft%20bokeh%20effect%20in%20background%2C%20vintage%20lamp%20casting%20gentle%20amber%20glow%20creating%20inviting%20welcoming%20atmosphere%2C%20minimalist%20composition%20with%20strong%20contrast%20between%20dark%20night%20shadows%20and%20warm%20light%20source%2C%20fine%20art%20documentary%20style%20photography%20with%20grainy%20texture%20evoking%20nostalgic%20feeling%20of%20late%20evening%20cafe%20culture&width=400&height=500&seq=art-light-night-003&orientation=portrait"
                    alt="Night Light"
                    className="w-full h-[300px] object-cover object-top"
                  />
                  <p className="text-xs mt-1 text-[#1a1a1a]/60 font-sans">Fig.3 — 20:00 Open</p>
                </div>
              </div>
            </div>
            
            <div className="md:col-span-8">
              <div className="space-y-8">
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="border-l-2 border-[#1a1a1a] pl-6">
                    <div className="text-xs font-sans uppercase tracking-wider mb-2 text-[#1a1a1a]/60">
                      Operating Days
                    </div>
                    <div className="text-lg mb-2">毎月 第二・第四</div>
                    <div className="text-sm text-[#8b0000]">※星が出ている日のみ</div>
                  </div>

                  <div className="border-l-2 border-[#1a1a1a] pl-6">
                    <div className="text-xs font-sans uppercase tracking-wider mb-2 text-[#1a1a1a]/60">
                      Hours
                    </div>
                    <div className="text-lg mb-2">20:00 オープン</div>
                    <div className="text-sm text-[#1a1a1a]/60">※夜の営業です</div>
                  </div>

                  <div className="border-l-2 border-[#1a1a1a] pl-6">
                    <div className="text-xs font-sans uppercase tracking-wider mb-2 text-[#1a1a1a]/60">
                      Notice
                    </div>
                    <div className="text-lg mb-2">あたたかい服装で</div>
                    <div className="text-sm text-[#1a1a1a]/60">お越しください</div>
                  </div>

                  <div className="border-l-2 border-[#1a1a1a] pl-6">
                    <div className="text-xs font-sans uppercase tracking-wider mb-2 text-[#1a1a1a]/60">
                      Facilities
                    </div>
                    <div className="text-lg mb-2">イートインスペース</div>
                    <div className="text-sm text-[#1a1a1a]/60">あり</div>
                  </div>
                </div>

                <div className="bg-[#1a1a1a] text-white p-6 rotate-[-1deg]">
                  <p className="text-sm leading-relaxed font-sans">
                    営業日は天候により変更となる場合がございます。<br />
                    Instagramにて最新情報をご確認ください。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Information Section */}
      <section className="py-20 px-6 bg-white border-t-2 border-[#1a1a1a]">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <h2 className="text-sm font-sans uppercase tracking-[0.3em] mb-2">Location</h2>
              <div className="text-2xl font-serif mb-4">会場</div>
            </div>
            
            <div className="md:col-span-8">
              <div className="space-y-6">
                <div className="border-l-2 border-[#1a1a1a] pl-6">
                  <div className="text-xs font-sans uppercase tracking-wider mb-2 text-[#1a1a1a]/60">
                    Address
                  </div>
                  <div className="text-base leading-relaxed">
                    〒370-3606<br />
                    群馬県北群馬郡吉岡町上野田1950
                  </div>
                </div>

                <div className="border-l-2 border-[#1a1a1a] pl-6">
                  <div className="text-xs font-sans uppercase tracking-wider mb-2 text-[#1a1a1a]/60">
                    Contact
                  </div>
                  <div className="text-base mb-2">
                    <a href="tel:09041799123" className="hover:text-[#8b0000] transition-colors cursor-pointer">
                      TEL: 090-4179-9123
                    </a>
                  </div>
                  <div className="text-base">
                    <a 
                      href="https://instagram.com/hoshizora1121" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:text-[#8b0000] transition-colors cursor-pointer inline-flex items-center gap-2"
                    >
                      <i className="ri-instagram-line"></i>
                      <span>@hoshizora1121</span>
                    </a>
                  </div>
                </div>

                <div className="pt-4">
                  <button 
                    onClick={() => setShowMap(!showMap)}
                    className="border-2 border-[#1a1a1a] px-8 py-3 text-sm font-sans uppercase tracking-wider hover:bg-[#1a1a1a] hover:text-white transition-all cursor-pointer whitespace-nowrap"
                  >
                    {showMap ? 'Hide Map' : 'View Map'}
                  </button>
                </div>

                {showMap && (
                  <div className="mt-6 rotate-[1deg]">
                    <div className="bg-white p-3 shadow-lg border border-[#1a1a1a]/20">
                      <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3219.8!2d139.0!3d36.5!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x601e8b0c0c0c0c0d%3A0x0!2zMzbCsDMwJzAwLjAiTiAxMznCsDAwJzAwLjAiRQ!5e0!3m2!1sja!2sjp!4v1234567890!5m2!1sja!2sjp&q=36.5,139.0"
                        width="100%"
                        height="400"
                        style={{ border: 0 }}
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        className="w-full"
                      ></iframe>
                      <p className="text-xs mt-2 text-[#1a1a1a]/60 font-sans">Fig.4 — 群馬県北群馬郡吉岡町上野田1950</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 border-t-2 border-[#1a1a1a]">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
            <div>
              <div className="text-xs font-sans uppercase tracking-[0.3em] mb-2 text-[#1a1a1a]/60">
                Night-only Coffee Stand
              </div>
              <div className="text-2xl font-serif mb-2">
                伊香保星空珈琲
              </div>
              <div className="text-sm text-[#1a1a1a]/60 font-sans italic">
                IKAHO HOSHIZORA COFFEE
              </div>
            </div>

            <div className="text-right">
              <div className="text-lg mb-2 font-serif">
                See you on a clear night.
              </div>
              <div className="text-sm text-[#1a1a1a]/60">
                星の夜に、またお会いしましょう
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-[#1a1a1a]/20 text-center">
            <div className="text-xs text-[#1a1a1a]/60 font-sans">
              <p className="mb-2">&copy; 2025 IKAHO HOSHIZORA COFFEE. All rights reserved.</p>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#8b0000] transition-colors cursor-pointer uppercase tracking-wider">
                Website Builder
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
